import React, { useState } from 'react';
import { ItemsTableDefault } from '../../../../Various/ItemsTable/ItemsTable';
import ActionButton from '../shared/ActionButton';

const ActiveCampaignsPerformanceTab = ({ wsProvider }) => {
    const [performanceData, setPerformanceData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortColumns, setSortColumns] = useState([]);

    const loadPerformanceData = () => {
        if (!wsProvider) {
            setError('WebSocket provider not available');
            return;
        }

        setIsLoading(true);
        setError(null);

        wsProvider.ws.waitForSocketAndSend({
            action: "getAllActiveCampaignsPerformanceAnalysis",
            options: {
                project_id: wsProvider.selectedProjectId,
                project_path: wsProvider.selectedProjectPath,
            }
        }, (response) => {
            setIsLoading(false);            
            if (response.error) {
                setError(response.error);
            } else {
                // Response should be an array of flat dictionaries
                // Handle different response structures
                let data = [];
                if (Array.isArray(response)) {
                    data = response;
                } else if (response.campaigns_performance && Array.isArray(response.campaigns_performance)) {
                    data = response.campaigns_performance;
                } else if (response.data && Array.isArray(response.data)) {
                    data = response.data;
                } else if (response.campaigns && Array.isArray(response.campaigns)) {
                    data = response.campaigns;
                } else if (response.results && Array.isArray(response.results)) {
                    data = response.results;
                }
                setPerformanceData(data);
            }
        });
    };

    // Generate column settings from the first row's keys if data exists
    const getColumnSettings = () => {
        if (!performanceData || performanceData.length === 0) {
            return {};
        }

        const firstRow = performanceData[0];
        const columns = {};
        
        Object.keys(firstRow).forEach((key) => {
            // Skip internal keys used by ItemsTable
            if (key.startsWith('#')) {
                return;
            }
            columns[key] = {
                title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                dataKey: key,
                width: 150,
                sortable: true
            };
        });

        return columns;
    };

    // Convert array to table data format
    const getTableData = () => {
        if (!performanceData || performanceData.length === 0) {
            return [];
        }

        const tableData = performanceData.map((row, index) => ({
            ...row,
            '#index': index,
            '#orig': row
        }));
        
        return tableData;
    };

    if (isLoading) {
        return (
            <div style={{ padding: '16px' }}>
                <div className="text-center p-4">
                    <p>Loading active campaigns performance analysis...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '16px' }}>
                <div className="text-center p-4">
                    <p style={{ color: '#dc3545' }}>Error: {error}</p>
                    <div style={{ marginTop: '8px' }}>
                        <ActionButton label="Retry" onClick={loadPerformanceData} variant="primary" size="md" />
                    </div>
                </div>
            </div>
        );
    }

    if (!performanceData || performanceData.length === 0) {
        return (
            <div style={{ padding: '16px' }}>
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <strong>Active Campaigns Performance Analysis</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                                Click the button below to load performance analysis for all active campaigns.
                            </p>
                        </div>
                        <button 
                            onClick={loadPerformanceData}
                            disabled={isLoading}
                            style={{
                                backgroundColor: isLoading ? '#6c757d' : '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                minWidth: '150px'
                            }}
                            onMouseOver={(e) => {
                                if (!isLoading) {
                                    e.target.style.backgroundColor = '#0056b3';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!isLoading) {
                                    e.target.style.backgroundColor = '#007bff';
                                }
                            }}
                        >
                            {isLoading ? 'Loading...' : 'Load Performance Data'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h5>Active Campaigns Performance Analysis</h5>
                    <p style={{ fontSize: '12px', color: '#6c757d', margin: 0 }}>
                        {performanceData.length} campaign{performanceData.length !== 1 ? 's' : ''} found
                    </p>
                </div>
                <button 
                    onClick={loadPerformanceData}
                    disabled={isLoading}
                    style={{
                        backgroundColor: isLoading ? '#6c757d' : '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        minWidth: '150px'
                    }}
                    onMouseOver={(e) => {
                        if (!isLoading) {
                            e.target.style.backgroundColor = '#0056b3';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!isLoading) {
                            e.target.style.backgroundColor = '#007bff';
                        }
                    }}
                >
                    {isLoading ? 'Loading...' : 'Reload Data'}
                </button>
            </div>
            <div style={{ height: "calc(100vh - 250px)", margin: "5px" }}>
                {(() => {
                    const tableData = getTableData();
                    const columnSettings = getColumnSettings();
                    return (
                        <ItemsTableDefault
                            tableData={tableData}
                            sortColumns={sortColumns}
                            setSortColumns={setSortColumns}
                            columnsSettings={columnSettings}
                            extraClass="campaigns_performance_table"
                            noMemo={false}
                        />
                    );
                })()}
            </div>
        </div>
    );
};

export default ActiveCampaignsPerformanceTab;

